import React from "react";
import { Head, usePage } from "@inertiajs/react";
import styled from "styled-components";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import PortalApp from "../../portal/App";
import {
  PortalContainer,
  PortalSection,
  PortalCard,
  PortalEmpty,
} from "../../portal/PortalCard";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import { SectionLoader } from "../../commons/Spinner";
import { apiGet } from "../../api";

export default function Company() {
  const {
    company,
    metrics = [],
    members = [],
    courses = [],
    chart_endpoints: chartEndpoints,
  } = usePage().props;

  const [memberSearch, setMemberSearch] = React.useState("");
  const filteredMembers = React.useMemo(() => {
    if (!memberSearch.trim()) return members;
    const q = memberSearch.toLowerCase();
    return members.filter(
      (m) =>
        m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q),
    );
  }, [members, memberSearch]);

  return (
    <>
      <Head>
        <title>Perusahaan — Taxsam Learning Center</title>
        <meta
          name="description"
          content="Pantau aktivitas pembelajaran anggota perusahaan."
        />
      </Head>
      <PortalContainer>
        {company && (
          <SectionErrorBoundary name="CompanyHeader">
            <CompanyHeader>
              <HeaderTop>
                <div>
                  <CompanyEyebrow>Dashboard Perusahaan</CompanyEyebrow>
                  <CompanyName>{company.name || "Perusahaan"}</CompanyName>
                </div>
                <HeaderMeta>
                  {company.subs_days != null && (
                    <SubsDaysPill $low={company.subs_days <= 30}>
                      {company.subs_days > 0
                        ? `${company.subs_days} hari tersisa`
                        : "Berakhir"}
                    </SubsDaysPill>
                  )}
                  {company.pending_users > 0 && (
                    <PendingBadge>{company.pending_users} pending</PendingBadge>
                  )}
                </HeaderMeta>
              </HeaderTop>
              {company.max_users != null && company.capacity_pct != null && (
                <CapacityBar
                  role="progressbar"
                  aria-valuenow={company.capacity_pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <CapacityFill style={{ width: `${company.capacity_pct}%` }} />
                  <CapacityLabel>
                    {company.capacity_pct}% kapasitas terpakai · max{" "}
                    {company.max_users} anggota
                  </CapacityLabel>
                </CapacityBar>
              )}
            </CompanyHeader>
          </SectionErrorBoundary>
        )}

        <SectionErrorBoundary name="CompanyMetrics">
          <PortalSection title="Metrik Pembelajaran">
            {metrics.length > 0 ? (
              <MetricGrid>
                {metrics.map((m) => (
                  <MetricCard key={m.key}>
                    <MetricLabel>{m.label}</MetricLabel>
                    <MetricValue>{m.value ?? "—"}</MetricValue>
                    {m.detail && <MetricDetail>{m.detail}</MetricDetail>}
                  </MetricCard>
                ))}
              </MetricGrid>
            ) : (
              <PortalEmpty>Belum ada metrik tercatat.</PortalEmpty>
            )}
          </PortalSection>
        </SectionErrorBoundary>

        {courses.length > 0 && (
          <SectionErrorBoundary name="CourseBreakdown">
            <PortalSection
              title="Progres Per Kursus"
              subtitle="Persentase penyelesaian video, ujian, dan sertifikat per kursus."
            >
              <CourseGrid>
                {courses.map((c) => (
                  <CourseCard key={c.id}>
                    <CourseName>{c.name}</CourseName>
                    <ProgressRow>
                      <ProgressLabel>Video</ProgressLabel>
                      <ProgressTrack>
                        <ProgressFill style={{ width: `${c.video_pct}%` }} />
                      </ProgressTrack>
                      <ProgressPct>{c.video_pct}%</ProgressPct>
                    </ProgressRow>
                    <ProgressRow>
                      <ProgressLabel>Ujian</ProgressLabel>
                      <ProgressTrack>
                        <ProgressFill style={{ width: `${c.exam_pct}%` }} />
                      </ProgressTrack>
                      <ProgressPct>{c.exam_pct}%</ProgressPct>
                    </ProgressRow>
                    <ProgressRow>
                      <ProgressLabel>Sertifikat</ProgressLabel>
                      <ProgressTrack>
                        <ProgressFill style={{ width: `${c.cert_pct}%` }} />
                      </ProgressTrack>
                      <ProgressPct>{c.cert_pct}%</ProgressPct>
                    </ProgressRow>
                  </CourseCard>
                ))}
              </CourseGrid>
            </PortalSection>
          </SectionErrorBoundary>
        )}

        {chartEndpoints && (
          <SectionErrorBoundary name="CompanyCharts">
            <PortalSection
              title="Grafik Ujian"
              subtitle="Visualisasi hasil ujian anggota perusahaan."
            >
              <ChartSection
                endpoints={chartEndpoints}
                companyId={company?.id}
                courses={courses}
              />
            </PortalSection>
          </SectionErrorBoundary>
        )}

        <SectionErrorBoundary name="CompanyMembers">
          <PortalSection
            title="Anggota Perusahaan"
            subtitle={`Menampilkan ${members.length} anggota teratas.`}
            action={
              members.length > 5 ? (
                <SearchInput
                  type="text"
                  placeholder="Cari nama atau email..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />
              ) : null
            }
          >
            {filteredMembers.length > 0 ? (
              <TableWrap>
                <MembersTable>
                  <thead>
                    <tr>
                      <th>Nama</th>
                      <th>Email</th>
                      <th>Materi</th>
                      <th>Ujian Lulus</th>
                      <th>Sertifikat</th>
                      <th>Peran</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((m) => (
                      <tr key={m.id}>
                        <td>{m.name}</td>
                        <td>{m.email}</td>
                        <td>
                          <MemberStat>{m.lessons_watched ?? 0}</MemberStat>
                        </td>
                        <td>
                          <MemberStat>{m.exams_passed ?? 0}</MemberStat>
                        </td>
                        <td>
                          <MemberStat>{m.certificates ?? 0}</MemberStat>
                        </td>
                        <td>
                          <RolePill $admin={m.is_admin}>
                            {m.is_admin ? "Admin" : "Anggota"}
                          </RolePill>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </MembersTable>
              </TableWrap>
            ) : (
              <PortalEmpty>
                {memberSearch
                  ? "Tidak ada anggota yang cocok."
                  : "Belum ada anggota perusahaan terdaftar."}
              </PortalEmpty>
            )}
          </PortalSection>
        </SectionErrorBoundary>
      </PortalContainer>
    </>
  );
}

Company.layout = (page) => <PortalApp>{page}</PortalApp>;

function ChartSection({ endpoints, companyId, courses }) {
  const [chartData, setChartData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [selectedCourse, setSelectedCourse] = React.useState(courses?.[0]?.id);

  React.useEffect(() => {
    if (!endpoints?.chart_exam || !companyId || !selectedCourse) return;
    setLoading(true);
    apiGet(
      `${endpoints.chart_exam}?id=${companyId}&course_id=${selectedCourse}`,
    )
      .then(({ parsedResponse, statusCode }) => {
        if (statusCode === 200) setChartData(parsedResponse);
      })
      .finally(() => setLoading(false));
  }, [endpoints, companyId, selectedCourse]);

  if (!courses || courses.length === 0) return null;

  return (
    <div>
      {courses.length > 1 && (
        <ChartCourseSelect
          value={selectedCourse}
          onChange={(e) => setSelectedCourse(e.target.value)}
        >
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </ChartCourseSelect>
      )}
      {loading ? (
        <SectionLoader />
      ) : chartData ? (
        <ChartBarsWrap>
          {Array.isArray(chartData.categories) &&
            chartData.categories.map((cat, i) => {
              const passed = chartData.series?.[0]?.data?.[i] || 0;
              const failed = chartData.series?.[1]?.data?.[i] || 0;
              const notStarted = chartData.series?.[2]?.data?.[i] || 0;
              const total = passed + failed + notStarted || 1;
              return (
                <ChartBarRow key={cat}>
                  <ChartBarLabel title={cat}>{cat}</ChartBarLabel>
                  <ChartBarTrack>
                    {passed > 0 && (
                      <ChartBarSegment
                        $color="#16a34a"
                        style={{ width: `${(passed / total) * 100}%` }}
                        title={`Lulus: ${passed}`}
                      />
                    )}
                    {failed > 0 && (
                      <ChartBarSegment
                        $color="#dc2626"
                        style={{ width: `${(failed / total) * 100}%` }}
                        title={`Gagal: ${failed}`}
                      />
                    )}
                    {notStarted > 0 && (
                      <ChartBarSegment
                        $color="#d1d5db"
                        style={{ width: `${(notStarted / total) * 100}%` }}
                        title={`Belum: ${notStarted}`}
                      />
                    )}
                  </ChartBarTrack>
                </ChartBarRow>
              );
            })}
          <ChartLegend>
            <LegendItem>
              <LegendDot $color="#16a34a" />
              Lulus
            </LegendItem>
            <LegendItem>
              <LegendDot $color="#dc2626" />
              Gagal
            </LegendItem>
            <LegendItem>
              <LegendDot $color="#d1d5db" />
              Belum Mulai
            </LegendItem>
          </ChartLegend>
        </ChartBarsWrap>
      ) : (
        <PortalEmpty>Belum ada data grafik tersedia.</PortalEmpty>
      )}
    </div>
  );
}

// ---- Styles ----------------------------------------------------------------

const CompanyHeader = styled(PortalCard)`
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border: 1px solid rgba(216, 89, 7, 0.32);
  display: flex;
  flex-direction: column;
  gap: 8px;

  [data-theme="dark"] & {
    background: linear-gradient(
      135deg,
      rgba(216, 89, 7, 0.18),
      rgba(247, 179, 12, 0.08)
    );
    border-color: rgba(216, 89, 7, 0.4);
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;

  @media ${narrowScreenMediaQuery} {
    flex-direction: column;
    gap: 8px;
  }
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CompanyEyebrow = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${brand_orange_deep};
`;

const CompanyName = styled.h2`
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  color: #1f2937;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const SubsDaysPill = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${({ $low }) =>
    $low ? "rgba(245, 158, 11, 0.16)" : "rgba(16, 185, 129, 0.16)"};
  color: ${({ $low }) => ($low ? "#b45309" : "#047857")};
`;

const PendingBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(245, 158, 11, 0.16);
  color: #b45309;
`;

const CapacityBar = styled.div`
  position: relative;
  height: 28px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  overflow: hidden;
  margin-top: 8px;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CapacityFill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  background: ${brand_gradient};
  transition: width 320ms ease;
`;

const CapacityLabel = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 14px;
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
  z-index: 1;
  mix-blend-mode: difference;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
`;

const MetricCard = styled(PortalCard)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-top: 3px solid transparent;
  border-image: ${brand_gradient} 1;
`;

const MetricLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const MetricValue = styled.span`
  font-size: 28px;
  font-weight: 800;
  color: ${brand_orange_deep};
  font-variant-numeric: tabular-nums;
`;

const MetricDetail = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 14px;
`;

const CourseCard = styled(PortalCard)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CourseName = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #1f2937;
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const ProgressRow = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr 40px;
  gap: 10px;
  align-items: center;
`;

const ProgressLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  overflow: hidden;
  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${brand_gradient};
  border-radius: 999px;
  transition: width 400ms ease;
`;

const ProgressPct = styled.span`
  font-size: 12px;
  font-weight: 800;
  color: ${brand_orange_deep};
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const SearchInput = styled.input`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  font-size: 12px;
  font-weight: 600;
  width: 220px;
  transition: border-color 150ms ease;

  &:focus {
    outline: none;
    border-color: ${brand_orange_deep};
    box-shadow: 0 0 0 3px rgba(216, 89, 7, 0.12);
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const MembersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: ${card_shadow_md};

  th,
  td {
    padding: 12px 18px;
    text-align: left;
    font-size: 13px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  }

  th {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6b7280;
    background: rgba(15, 23, 42, 0.02);
  }

  td {
    color: #1f2937;
  }

  tbody tr:hover {
    background: #fff7ed;
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    box-shadow: none;
    th {
      color: #9ca3af;
      background: rgba(255, 255, 255, 0.04);
    }
    td {
      color: #e5e7eb;
      border-bottom-color: rgba(255, 255, 255, 0.06);
    }
    tbody tr:hover {
      background: rgba(216, 89, 7, 0.06);
    }
  }
`;

const MemberStat = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: ${brand_orange_deep};
`;

const RolePill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${({ $admin }) =>
    $admin ? "rgba(216, 89, 7, 0.14)" : "rgba(15, 23, 42, 0.06)"};
  color: ${({ $admin }) => ($admin ? brand_orange_deep : "#4b5563")};
`;

const ChartCourseSelect = styled.select`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 16px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${brand_orange_deep};
  }
`;

const ChartBarsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ChartBarRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 12px;
  align-items: center;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 100px 1fr;
  }
`;

const ChartBarLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const ChartBarTrack = styled.div`
  display: flex;
  height: 20px;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.04);
`;

const ChartBarSegment = styled.div`
  height: 100%;
  background: ${({ $color }) => $color};
  transition: width 400ms ease;
`;

const ChartLegend = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;
